import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SessionView } from '@mdt/contracts';
import { isAdmin } from '@mdt/domain';
import { Button, Dialog, ErrorAlert } from '../components/kit.tsx';
import { SafeLink } from '../components/SafeLink.tsx';
import { BUILD_ID, command } from '../lib/api.ts';
import { anyDirty } from '../lib/dirty.ts';
import { useFormat } from '../lib/format.ts';
import { usePwa } from '../lib/pwa.tsx';
import { useSession } from '../lib/session.tsx';

/** The frame of every screen: skip link, header and navigation, banners, main landmark, footer. */
export function Shell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { state, signOut } = useSession();
  const signedIn = state.status === 'signedIn' ? state : null;
  const admin = signedIn ? isAdmin({ grants: signedIn.view.grants.map((g) => ({ grantId: '', ...g })) }) : false;
  const teacher = !!signedIn?.view.grants.some((g) => g.role === 'teacher');
  const staffRoles = !!signedIn?.view.grants.length;

  return (
    <>
      <SafeLink href="#main" className="skip-link">{t('shell.skip')}</SafeLink>
      <header className="app-header">
        <div className="app-header__inner">
          <SafeLink href="/" className="app-title">{t('app.title')}</SafeLink>
          {signedIn?.view.mode === 'shared' ? <span className="mode-badge">{t('shell.sharedBadge')}</span> : null}
          {signedIn ? (
            <nav className="app-nav" aria-label={t('shell.nav')}>
              <ul>
                <li><SafeLink href="/">{t('shell.home')}</SafeLink></li>
                {signedIn.view.person.kind === 'student' ? <li><SafeLink href="/group">{t('shell.group')}</SafeLink></li> : null}
                {signedIn.view.person.kind === 'student' ? <li><SafeLink href="/deliverables">{t('shell.deliverables')}</SafeLink></li> : null}
                {signedIn.view.person.kind === 'student' ? <li><SafeLink href="/matching">{t('shell.matching')}</SafeLink></li> : null}
                {signedIn.view.person.kind === 'student' ? <li><SafeLink href="/results">{t('shell.results')}</SafeLink></li> : null}
                {teacher ? <li><SafeLink href="/inbox">{t('shell.inbox')}</SafeLink></li> : null}
                {signedIn.view.person.kind === 'staff' ? <li><SafeLink href="/scoring">{t('shell.scoring')}</SafeLink></li> : null}
                {signedIn.view.person.kind === 'student' || signedIn.view.grants.length > 0
                  ? <li><SafeLink href="/clearance">{t('shell.clearance')}</SafeLink></li> : null}
                <li><SafeLink href="/schedule">{t('shell.schedule')}</SafeLink></li>
                {admin ? <li><SafeLink href="/admin">{t('shell.adminConsole')}</SafeLink></li> : null}
                {admin ? <li><SafeLink href="/admin/cohorts">{t('shell.adminCohorts')}</SafeLink></li> : null}
                {staffRoles ? <li><SafeLink href="/exports">{t('shell.exports')}</SafeLink></li> : null}
                <li><SafeLink href="/settings">{t('shell.settings')}</SafeLink></li>
                {admin ? <li><SafeLink href="/admin/notifications">{t('shell.adminNotifications')}</SafeLink></li> : null}
              </ul>
            </nav>
          ) : null}
          <LanguageToggle />
          {signedIn ? <Button variant="ghost" onClick={() => void signOut()}>{t('auth.signOut')}</Button> : null}
        </div>
      </header>
      {signedIn?.view.ghost ? <GhostBanner ghost={signedIn.view.ghost} /> : null}
      <Banners />
      <main id="main" tabIndex={-1}>{children}</main>
      <footer className="app-footer">
        <p>{t('shell.footer', { build: BUILD_ID })}</p>
      </footer>
      {signedIn ? <IdleWarning shared={signedIn.view.mode === 'shared'} /> : null}
    </>
  );
}

export function LanguageToggle() {
  const { t, i18n } = useTranslation();
  const { setLanguage } = useSession();
  return (
    <div className="toggle-group" role="group" aria-label={t('shell.language')}>
      <button type="button" lang="th" aria-pressed={i18n.language === 'th'} onClick={() => setLanguage('th')}>ไทย</button>
      <button type="button" lang="en" aria-pressed={i18n.language !== 'th'} onClick={() => setLanguage('en')}>English</button>
    </div>
  );
}

/**
 * The ghost-mode banner (roadmap 11.1, GHO-04): on every screen while an administrator views or acts as someone, it says
 * so, who started it and when it ends, with the way out. It cannot be dismissed; it is a labelled region so a screen
 * reader can find it from anywhere.
 */
function GhostBanner({ ghost }: { ghost: NonNullable<SessionView['ghost']> }) {
  const { t } = useTranslation();
  const f = useFormat();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const exit = async () => {
    setBusy(true);
    try {
      await command('ghost.end', {});
    } catch (e) {
      // an already-ended session is fine: the next request runs as the administrator again
      if (!(e && typeof e === 'object' && 'code' in e && ['CONFLICT', 'INVALID_STATE', 'UNAUTHENTICATED'].includes(String((e as { code: string }).code)))) {
        setError(e);
        setBusy(false);
        return;
      }
    }
    globalThis.location.assign('/admin/ghost');
  };
  return (
    <section className={`ghost-banner ghost-banner--${ghost.mode}`} aria-label={t('ghostBanner.label')} data-testid="ghost-banner">
      <p>
        <strong>{t(ghost.mode === 'act' ? 'ghostBanner.acting' : 'ghostBanner.viewing', { subject: ghost.subject.nameTh })}</strong>
        {' — '}{t('ghostBanner.startedBy', { admin: ghost.admin.nameTh })}{' — '}{t('ghostBanner.ends', { time: f.time(ghost.expiresAt) })}
      </p>
      <Button variant="danger" busy={busy} onClick={() => void exit()}>{t('ghostBanner.exit')}</Button>
      <ErrorAlert error={error} />
    </section>
  );
}

function useOnline() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    globalThis.addEventListener('online', on);
    globalThis.addEventListener('offline', off);
    return () => {
      globalThis.removeEventListener('online', on);
      globalThis.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** Update and offline notices. Polite live region: they never interrupt what someone is reading. */
function Banners() {
  const { t } = useTranslation();
  const pwa = usePwa();
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  return (
    <div className="banners" role="status" aria-live="polite">
      {!online ? <div className="alert alert--warning"><p>{t('shell.offline')}</p></div> : null}
      {pwa.updateReady || pwa.outdated ? (
        <div className="alert" data-testid="update-banner">
          <p><strong>{t('update.ready')}</strong> {anyDirty() ? t('update.draftsKept') : t('update.whenReady')}</p>
          <Button busy={busy} onClick={() => {
            setBusy(true);
            void pwa.applyUpdate();
          }}>{t('update.reload')}</Button>
        </div>
      ) : null}
    </div>
  );
}

function useNow(everyMs: number, active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [everyMs, active]);
  return now;
}

/** The idle warning (roadmap 3.4): 2 minutes before the deadline; only its own buttons keep the session. */
function IdleWarning({ shared }: { shared: boolean }) {
  const { t } = useTranslation();
  const f = useFormat();
  const { idle, signOut } = useSession();
  const open = idle.phase === 'warning';
  const now = useNow(1000, open);
  const left = Math.max(0, Math.ceil((idle.deadline - now) / 1000));
  const mmss = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
  const stay = () => void idle.stay();
  return (
    <Dialog
      open={open}
      alert
      title={t('idle.title')}
      onCancel={stay}
      actions={(
        <>
          <Button onClick={stay}>{t('idle.stay')}</Button>
          <Button variant="secondary" onClick={() => void signOut()}>{t('idle.signOutNow')}</Button>
        </>
      )}
    >
      <p className="countdown" aria-hidden="true" data-testid="idle-countdown">{mmss}</p>
      <p>{t('idle.body', { time: f.time(idle.deadline) })}</p>
      {shared ? <p>{t('idle.sharedNote')}</p> : null}
    </Dialog>
  );
}
